import { Navigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@rackd/ui';
import { useAuth } from './AuthProvider';
import { routes } from '@/config/routes';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Protects routes that require admin access.
 * Redirects to login if not authenticated.
 * Shows access denied if authenticated but not an admin.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--background)]">
        <div className="space-y-4">
          <Skeleton className="h-12 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={routes.login} state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

function AccessDenied() {
  const { signOut } = useAuth();

  return (
    <div className="flex items-center justify-center h-screen bg-[var(--background)]">
      <div className="text-center max-w-md p-8">
        <div className="w-16 h-16 bg-[var(--error)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-[var(--error)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[var(--text)] mb-2">Access Denied</h1>
        <p className="text-[var(--text-tertiary)] mb-6">
          You do not have administrator privileges. This portal is only accessible to Rackd admins.
        </p>
        <button
          onClick={signOut}
          className="px-4 py-2 text-sm font-medium text-[var(--text-tertiary)] hover:text-[var(--text)] transition-colors"
        >
          Sign out and try another account
        </button>
      </div>
    </div>
  );
}
